<template>
  <div id="app">
    <public-layout v-if="isPublicRoute">
      <router-view />
    </public-layout>
    <auth-view v-else-if="!user" />
    <template v-else>
      <admin-layout v-if="user.role === 'admin'">
        <router-view />
      </admin-layout>
      <manager-layout v-else-if="user.role === 'manager'">
        <router-view />
      </manager-layout>
      <customer-layout
        v-else-if="['company-user', 'company-owner'].includes(user.role)"
      >
        <router-view />
      </customer-layout>
      <patient-layout v-else-if="user.role === 'patient'">
        <router-view />
      </patient-layout>
      <blogger-layout v-else-if="user.role === 'blogger'">
        <router-view />
      </blogger-layout>
    </template>
    <v-dialog
      v-if="newModalFormVisible"
      :value="newModalFormVisible"
      :width="modalFormComponent.width || 400"
      :content-class="
        (modalFormComponent.contentClass,
        'ma-2 ma-sm-6' || 'rounded-xl ma-2 ma-sm-6')
      "
      @input="cancelNewModalForm()"
    >
      <component
        :is="modalFormComponent.component"
        v-bind="modalFormComponent"
        @confirm="confirmNewModalForm"
        @cancel="cancelNewModalForm"
      />
    </v-dialog>
    <v-dialog
      v-if="newsModalVisible"
      :value="newsModalVisible"
      :width="600"
      content-class="rounded-xl ma-2 ma-sm-6"
      persistent
    >
      <news-modal :news="currentNews" @ok="handleNewsOk" />
    </v-dialog>
    <v-footer
      style="
        background-color: #1a2947;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 6;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      "
    >
      <img src="/logo.svg" width="30" height="30" style="margin-right: 10px" />
    </v-footer>
  </div>
</template>
<script>
import AuthView from "@/views/AuthView";
import AdminLayout from "@/layouts/AdminLayout";
import ManagerLayout from "@/layouts/ManagerLayout";
import CustomerLayout from "@/layouts/CustomerLayout";
import PatientLayout from "@/layouts/PatientLayout";
import BloggerLayout from "@/layouts/BloggerLayout";
import PublicLayout from "@/layouts/PublicLayout.vue";
import NewsModal from "@/components/NewsModal.vue";
import { mapActions, mapGetters } from "vuex";
import NSApi from "@/api/NSApi";

export default {
  components: {
    PatientLayout,
    CustomerLayout,
    ManagerLayout,
    AdminLayout,
    BloggerLayout,
    AuthView,
    NewsModal,
    PublicLayout,
  },
  computed: {
    ...mapGetters(["user"]),
    isPublicRoute() {
      return (
        this.$route &&
        ["privacy", "agreement", "session-by-url"].includes(this.$route.name)
      );
    },
    ...mapGetters("modals", ["newModalFormVisible", "modalFormComponent"]),
    ...mapGetters("companies", ["companies"]),
  },
  watch: {
    user: {
      handler(newUser) {
        this.talkMeRetryCount = 0;
        if (newUser && newUser.company_id) {
          this.$store.dispatch("companies/request_companies").then(() => {
            this.setTalkMeClientInfo();
          });
        } else if (newUser) {
          this.setTalkMeClientInfo();
        }
        if (newUser) {
          this.checkAndShowNews();
        }
      },
      immediate: true,
    },
    companies: {
      handler() {
        if (this.user) {
          this.setTalkMeClientInfo();
        }
      },
    },
  },
  methods: {
    ...mapActions(["doLogin"]),
    ...mapActions(["doGetUser"]),
    ...mapActions("modals", ["cancelNewModalForm", "confirmNewModalForm"]),
    ...mapActions("companies", ["request_companies"]),

    setTalkMeClientInfo() {
      if (!this.user) {
        return;
      }

      if (typeof window.TalkMe === "undefined") {
        if (this.talkMeRetryCount < this.maxTalkMeRetries) {
          this.talkMeRetryCount++;
          setTimeout(() => this.setTalkMeClientInfo(), 500);
        }
        return;
      }

      this.talkMeRetryCount = 0;

      const name = [this.user.first_name, this.user.last_name]
        .filter(Boolean)
        .join("_");

      const company = this.user.company_id
        ? this.companies.find((c) => c.id === this.user.company_id)
        : null;

      const clientInfo = {
        name: name || undefined,
        phone: this.user.phone || undefined,
        email: this.user.email || undefined,
        custom: {},
      };

      if (company && company.name) {
        clientInfo.custom.company = company.name;
      }

      if (Object.keys(clientInfo.custom).length === 0) {
        delete clientInfo.custom;
      }

      window.TalkMe("setClientInfo", clientInfo, function (error) {
        if (error) {
          console.log("Ошибка setClientInfo", error);
        }
      });
    },
    async checkAndShowNews() {
      if (!this.user) return;

      try {
        const [allNews, readNewsIds] = await Promise.all([
          NSApi.instance.getNews({ scope: "internal" }),
          NSApi.instance.getViewedNewsIds(),
        ]);
        const unreadNews = allNews.filter(
          (news) => news.active && !readNewsIds.includes(news.id)
        );

        if (unreadNews.length > 0) {
          this.pendingNews = unreadNews;
          this.showNextNews();
        }
      } catch (error) {
        console.error("Error loading news:", error);
      }
    },
    showNextNews() {
      if (this.pendingNews.length > 0) {
        this.currentNews = this.pendingNews[0];
        this.newsModalVisible = true;
      }
    },
    async handleNewsOk(newsId) {
      try {
        await NSApi.instance.markNewsViewed(newsId);
      } catch (error) {
        console.error("Error marking news as viewed:", error);
      }
      this.newsModalVisible = false;
      this.pendingNews = this.pendingNews.filter((n) => n.id !== newsId);

      if (this.pendingNews.length > 0) {
        setTimeout(() => {
          this.showNextNews();
        }, 300);
      }
    },
  },
  beforeMount() {
    if (!this.$router.history._startLocation.includes("session-by-url")) {
      this.doGetUser();
    }
  },
  data() {
    return {
      talkMeRetryCount: 0,
      maxTalkMeRetries: 10,
      newsModalVisible: false,
      currentNews: null,
      pendingNews: [],
    };
  },
};
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.border-radius-30px.v-dialog {
  border-radius: 30px;
}

.snackbar .v-snack__content {
  text-align: center;
}

.v-treeview-node .v-treeview-node--leaf {
  margin-left: -30px;
}
</style>
